import { action } from '@ember/object';
import Component from '@glimmer/component';
import { modifier } from 'ember-modifier';
import type { Select } from 'ember-power-select/components/power-select';
import PowerSelectOptions, { type PowerSelectOptionsArgs } from 'ember-power-select/components/power-select/options';

interface ScrollAwareOptionsSignature<T> {
  Args: PowerSelectOptionsArgs & {
    onScroll: (scrolledPercent: number) => void;
  };
  Blocks: {
    default: [option: T, select: Select];
  };
  Element: HTMLElement;
}

const scrollHandler = modifier((element: HTMLElement, [handler]: [(event: Event) => void]) => {
  const optionsElement = element.parentElement?.getElementsByClassName('ember-power-select-options')[0];
  if (!optionsElement) return;

  optionsElement.addEventListener('scroll', handler, { passive: true });
  return () => {
    optionsElement.removeEventListener('scroll', handler);
  };
});

export default class ScrollAwareOptions<T> extends Component<ScrollAwareOptionsSignature<T>> {
  @action
  handleScroll(e: Event): void {
    const listElement = e.target! as HTMLElement;
    const scrolledPercent = listElement.scrollTop / (listElement.scrollHeight - listElement.offsetHeight);
    this.args.onScroll(scrolledPercent);
  }

  <template>
    {{! This should match attributes passed here: https://github.com/cibernox/ember-power-select/blob/master/ember-power-select/src/components/power-select.hbs#L234 }}
    <PowerSelectOptions
      {{scrollHandler this.handleScroll}}
      @loadingMessage={{@loadingMessage}}
      @select={{@select}}
      @options={{@options}}
      @optionsComponent={{@optionsComponent}}
      @extra={{@extra}}
      @highlightOnHover={{@highlightOnHover}}
      @groupComponent={{@groupComponent}}
      ...attributes
      as |option select|
    >
      {{yield option select}}
    </PowerSelectOptions>
  </template>
}
